import { useCallback, useState, useRef, useEffect } from 'react'
import { toast } from 'react-toastify'
import { SendLogo } from '../../../../Services/FileService'
import { useConfig } from '../../../../Providers/ConfigProvider'
import { useAuth } from '../../../../Providers/AuthProvider'

const useLogoUploader = ({
    defaultIconUrl = '/Icons/uploadImg.svg',
    allowedExtensions = ['.png', '.jpg', '.jpeg'],
    allowedFileTypes = ['image/png', 'image/jpeg'],
    setDeleteLogoTrigger
}) => {
    const { api } = useConfig()
    const { authState } = useAuth()
    const [selectedFile, setSelectedFile] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(defaultIconUrl)
    const [isDragging, setIsDragging] = useState(false)
    const fileInputRef = useRef(null)

    useEffect(() => {
        const currentPreviewUrl = previewUrl
        return () => {
            if (currentPreviewUrl && currentPreviewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(currentPreviewUrl)
            }
        }
    }, [previewUrl])

    useEffect(() => {
        setPreviewUrl(defaultIconUrl)
    }, [defaultIconUrl])

    const validateFile = useCallback((file) => {
        if (!file) {
            return false
        }
        const fileType = file.type
        const fileName = file.name.toLowerCase()
        const extension = fileName.substring(fileName.lastIndexOf('.'))
        const maxFileSize = 10 * 1024 * 1024 // 10 MB

        if (file.size > maxFileSize) {
            toast.warning(`Plik jest za duży (maksymalnie 10MB)`)
            return false
        }
        if (allowedFileTypes.includes(fileType) && allowedExtensions.includes(extension)) {
            return true
        } else {
            toast.warning(`Nieprawidłowy typ pliku`)
            setSelectedFile(null)
            setPreviewUrl(defaultIconUrl)
            if (fileInputRef.current) {
                fileInputRef.current.value = ""
            }
            return false
        }
    }, [allowedExtensions, allowedFileTypes, defaultIconUrl])

    const uploadFile = useCallback(async (fileToUpload) => {
        const promise = SendLogo(api, fileToUpload, authState.custId)
        toast.promise(
            promise,
            {
                pending: 'Wysyłanie logotypu...',
                success: {
                    render({ data }) {
                        if (data?.filePath || data?.newImageUrl || data?.logotypUrl) {
                            setPreviewUrl(data.filePath || data.newImageUrl || data.logotypUrl)
                        }
                        setSelectedFile(null)
                        if (setDeleteLogoTrigger) {
                            setDeleteLogoTrigger(false)
                        }
                        if (fileInputRef.current) {
                            fileInputRef.current.value = ""
                        }
                        return `Poprawnie wgrano logotyp`
                    }
                },
                error: {
                    render({ data }) {
                        setPreviewUrl(defaultIconUrl)
                        setSelectedFile(null)
                        if (fileInputRef.current) {
                            fileInputRef.current.value = ""
                        }
                        return `Błąd w trakcie wgrywania logotypu`
                    }
                }
            }
        )
    }, [authState.custId, defaultIconUrl])

    const processFile = useCallback((file) => {
        if (validateFile(file)) {
            setSelectedFile(file)
            const newPreviewUrl = URL.createObjectURL(file)
            setPreviewUrl(newPreviewUrl)
            uploadFile(file)
        }
    }, [validateFile, uploadFile])

    const handleFileChange = useCallback((event) => {
        const file = event.target.files && event.target.files[0]
        if (file) {
            processFile(file)
        }
    }, [processFile])

    const handleDrop = useCallback((event) => {
        event.preventDefault()
        event.stopPropagation()
        setIsDragging(false)
        const file = event.dataTransfer.files && event.dataTransfer.files[0]
        if (file) {
            processFile(file)
            if (fileInputRef.current) {
                fileInputRef.current.files = event.dataTransfer.files
            }
        }
    }, [processFile])

    const handleDragOver = useCallback((event) => {
        event.preventDefault()
        event.stopPropagation()
        if (!isDragging) setIsDragging(true)
    }, [isDragging])

    const handleDragLeave = useCallback((event) => {
        event.preventDefault()
        event.stopPropagation()
        if (isDragging) setIsDragging(false)
    }, [isDragging])

    const handleSelectFile = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }, [])

    const getRootProps = useCallback(() => ({
        onClick: handleSelectFile,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
        role: "button",
        tabIndex: 0,
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectFile() },
    }), [handleSelectFile, handleDragOver, handleDragLeave, handleDrop])

    const getInputProps = useCallback(() => ({
        type: "file",
        ref: fileInputRef,
        onChange: handleFileChange,
        accept: allowedExtensions.join(','),
        style: { display: 'none' }
    }), [handleFileChange, allowedExtensions])

    // useEffect(() => { //Obsługa ctrl + v na stronie DK : Zablokowane z powodu dziwnego achowania z kopiowaniem tekstu z one note
    //     const handlePaste = (event) => {
    //         const items = event.clipboardData?.items
    //         if (!items) {
    //             return
    //         }
    //         for (let i = 0; i < items.length; i++) {
    //             const item = items[i]
    //             if (item.type.startsWith("image/")) {
    //                 const file = item.getAsFile()
    //                 if (file) {
    //                     processFile(file)
    //                 }
    //                 break// obsłuż tylko jeden obrazek
    //             }
    //         }
    //     }
    //     window.addEventListener("paste", handlePaste)
    //     return () => window.removeEventListener("paste", handlePaste)
    // }, [processFile])

    return {
        selectedFile,
        previewUrl,
        isDragging,
        getRootProps,
        getInputProps,
    }
}

export default useLogoUploader